<template>
  <sticky-sidebar width="250px">
    <div class="py-5 px-0">
      <div class="links-list py-0 pr-4 pl-2">
        <p class="title my-0 mr-0 ml-2 text-[11px] tracking-[1px] uppercase font-semibold select-none text-grey-500">
          {{ $t(`components.quickLinkSidebar.navigation`) }}
        </p>
        <router-link v-for="anchor in anchors" :key="anchor.id" v-slot="{ navigate }" :to="`#${anchor.id}`" custom>
          <div
            :class="[
              'link-item py-1 px-2 cursor-pointer rounded-[2px] select-none',
              anchor.tagName.toLowerCase(),
              { active: activeAnchor && activeAnchor.id === anchor.id },
            ]"
            @click="navigate"
          >
            {{ anchor.textContent }}
          </div>
        </router-link>
      </div>
    </div>
  </sticky-sidebar>
</template>

<script lang="ts" setup>
import { nextTick, onBeforeUnmount, onMounted, PropType, ref } from 'vue';
import StickySidebar from '@/components/widgets/StickySidebar.vue';
import { getHeaderHeightInPx } from '@/utils/headerHeight';

const props = defineProps({ root: { type: Element as PropType<HTMLDivElement>, required: true } });
const anchors = ref([] as Element[]);
const activeAnchor = ref(null as Element | null);

// find the first element that is visible below the header
const getActiveAnchor = () => anchors.value.find(el => el.getBoundingClientRect().y > getHeaderHeightInPx()) ?? null;

const onWindowScroll = () => {
  activeAnchor.value = getActiveAnchor();
};

onMounted(async () => {
  await nextTick();
  anchors.value = Array.from(props.root.querySelectorAll('h1,h2,h3,h4')).filter(el => el.id && el.textContent);
  activeAnchor.value = getActiveAnchor();
  window.addEventListener('scroll', onWindowScroll);
});

onBeforeUnmount(() => {
  window.removeEventListener('scroll', onWindowScroll);
});
</script>

<style lang="scss" scoped>
.links-list {
  border-left: 1px solid lightgray;
}

.link-item {
  &.active {
    color: var(--dp-color-cta-500);
  }

  &:hover {
    background-color: var(--dp-color-neutral-50);
  }

  &.h1 {
    font: var(--vvd-typography-base-extended-bold);
  }

  &.h2 {
    margin-left: 8px;
    font: var(--vvd-typography-base);
  }

  &.h3 {
    margin-left: 24px;
    font: var(--vvd-typography-base-condensed);
  }

  &.h4 {
    margin-left: 32px;
    font: var(--vvd-typography-base-condensed);
  }
}
</style>
